import { SelectorEngine } from '@runtime/SelectorEngine';
import { MAX_TEXT_PREVIEW } from '@shared/constants';
import { Toast } from '../ui/Toast';

export class ElementPicker {
  private selectorEngine: SelectorEngine;
  private isPicking = false;
  private highlightElement: HTMLElement | null = null;
  private onElementSelect?: (element: Element, selector: string) => void;

  constructor(selectorEngine: SelectorEngine) {
    this.selectorEngine = selectorEngine;
    this.createHighlightElement();
  }

  private createHighlightElement(): void {
    this.highlightElement = document.createElement('div');
    this.highlightElement.className = 'ce-highlight';
    this.highlightElement.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 999999998;
      border: 2px solid #00e5ff;
      border-radius: 6px;
      box-shadow: 0 0 0 9999px rgba(0,0,0,0.08);
      display: none;
    `;
    document.documentElement.appendChild(this.highlightElement);
  }

  start(onElementSelect: (element: Element, selector: string) => void): void {
    if (this.isPicking) return;

    this.isPicking = true;
    this.onElementSelect = onElementSelect;

    document.addEventListener('mousemove', this.onMouseMove, true);
    document.addEventListener('click', this.onClick, true);
  }

  stop(): void {
    if (!this.isPicking) return;

    this.isPicking = false;
    this.hideHighlight();

    document.removeEventListener('mousemove', this.onMouseMove, true);
    document.removeEventListener('click', this.onClick, true);
  }

  private onMouseMove = (event: MouseEvent): void => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    if (!element || this.isEditorElement(element)) {
      this.hideHighlight();
      return;
    }

    this.updateHighlight(element);
  };

  private onClick = (event: MouseEvent): void => {
    const element = document.elementFromPoint(event.clientX, event.clientY);

    // First check: if the click target is inside the panel, don't select
    const clickTarget = event.target as Element;
    if (clickTarget.closest('.ce-panel')) {
      return;
    }

    if (!element || this.isEditorElement(element)) return;

    event.preventDefault();
    event.stopPropagation();

    const tag = element.tagName.toLowerCase();
    if (tag === 'html' || tag === 'body') {
      Toast.show('Select a specific element (not ' + tag + ')', 'warning');
      return;
    }

    const selector = this.selectorEngine.generate(element);
    if (this.onElementSelect) {
      this.onElementSelect(element, selector);
    }
  };

  private updateHighlight(element: Element): void {
    if (!this.highlightElement) return;

    const rect = element.getBoundingClientRect();
    this.highlightElement.style.display = 'block';
    this.highlightElement.style.top = Math.max(0, rect.top) + 'px';
    this.highlightElement.style.left = Math.max(0, rect.left) + 'px';
    this.highlightElement.style.width = Math.max(0, rect.width) + 'px';
    this.highlightElement.style.height = Math.max(0, rect.height) + 'px';
  }

  private hideHighlight(): void {
    if (this.highlightElement) {
      this.highlightElement.style.display = 'none';
    }
  }

  private isEditorElement(element: Element): boolean {
    return (
      element.classList.contains('ce-panel') ||
      element.closest('.ce-panel') !== null ||
      element.classList.contains('ce-highlight') ||
      element.closest('.ce-highlight') !== null
    );
  }

  getSmartValue(element: Element, action: string): string {
    switch (action) {
      case 'set_text':
        return ((element as HTMLElement).innerText || '').trim().slice(0, MAX_TEXT_PREVIEW);

      case 'replace_image':
        if (element instanceof HTMLImageElement) {
          return element.src || '';
        } else {
          // Handle background image - extract just the URL
          const computedStyle = window.getComputedStyle(element);
          const backgroundImage = computedStyle.backgroundImage;

          // Extract URL from background-image: url("path/to/image.jpg")
          const urlMatch = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/);
          return urlMatch ? urlMatch[1] : '';
        }

      default:
        return '';
    }
  }

  detectAction(element: Element): string {
    if (element instanceof HTMLImageElement) {
      return 'replace_image';
    }

    // Check for background image (only real images, not gradients or CSS variables)
    const computedStyle = window.getComputedStyle(element);
    const backgroundImage = computedStyle.backgroundImage;
    const hasBackgroundImage =
      backgroundImage &&
      backgroundImage !== 'none' &&
      backgroundImage !== 'initial' &&
      backgroundImage.includes('url(') &&
      !backgroundImage.includes('gradient');

    if (hasBackgroundImage) {
      return 'replace_image';
    }

    const textContent = (element.textContent || '').trim();
    const hasText = textContent.length > 0;

    if (hasText) {
      const tagName = element.tagName.toLowerCase();
      const isContainer = [
        'div',
        'span',
        'section',
        'article',
        'main',
        'header',
        'footer',
        'nav',
        'aside',
      ].includes(tagName);

      if (isContainer && textContent.length < 10) {
        return 'hide';
      }

      return 'set_text';
    }

    return 'hide';
  }

  destroy(): void {
    this.stop();
    if (this.highlightElement) {
      this.highlightElement.remove();
      this.highlightElement = null;
    }
  }
}
